/* XNote proposal Home (docs/design-v3-light.md §5; two-product revision of 2026-09-30,
   docs/build/feedback-0929/home-r3.md §5). No dependencies.
   How it works (sections/xp-home-howitworks): the real demo film plays in place, and the four steps are its chapters.
   - Two films, one shown per breakpoint (≥ 768 px the desktop cut, phones the portrait cut); the script always drives
     the visible one and pauses the other. Both have no sound track, so they only ever play muted.
   - Autoplay: muted, looping, only while the film is ≥ 50 % in view, after `load`, with the tab visible; never with
     reduced motion or Save-Data (then the poster shows with a centred Play button and nothing loads until pressed).
   - The corner button plays / pauses; a pause by the viewer sticks for the visit. A step click seeks to its chapter
     (data-d / data-m, seconds) and plays, also under reduced motion (the viewer asked for it).
   - The active step follows the film's time; each step's hairline fills while its chapter plays (past chapters full).
   Without this script the films keep their native controls and the steps are plain text buttons that do nothing. */
(() => {
  'use strict';
  const d = document, w = window, root = d.documentElement;
  const $ = (s, el = d) => el.querySelector(s);
  const $$ = (s, el = d) => Array.from(el.querySelectorAll(s));
  const rmq = w.matchMedia('(prefers-reduced-motion: reduce)');
  const conn = navigator.connection || {};
  const lite = () => root.classList.contains('xp-lite') || Boolean(conn.saveData) || /2g$/.test(conn.effectiveType || '');
  const still = () => rmq.matches || lite();
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));

  function initHow(sec) {
    const film = $('[data-hiw-film]', sec);
    if (!film) return;
    const vids = $$('video[data-hiw-video]', film);
    const btn = $('[data-hiw-play]', film);
    const steps = $$('.xp-hiw-step', sec);
    if (!vids.length || !btn) return;
    film.setAttribute('data-hiw-ready', '');
    vids.forEach((v) => { v.removeAttribute('controls'); v.loop = true; v.muted = true; });
    btn.hidden = false;
    let visible = false, loaded = d.readyState === 'complete', userPaused = false, userPlay = false, raf = 0;
    const shown = () => vids.find((v) => v.getClientRects().length > 0) || vids[0];
    const key = (v) => v.dataset.hiwVideo || 'd';
    const marks = (k) => steps.map((s) => Number(s.dataset[k]) || 0);
    const wants = () => !userPaused && visible && !d.hidden && (userPlay || (loaded && !still()));
    const idle = () => film.classList.toggle('is-idle', still() && !userPlay);
    idle();

    function update(v) {
      const k = key(v), t = v.currentTime || 0, dur = v.duration || 0;
      const at = marks(k);
      let cur = 0;
      at.forEach((s, i) => { if (t >= s) cur = i; });
      steps.forEach((s, i) => {
        const on = i === cur;
        s.classList.toggle('is-on', on);
        if (on) s.setAttribute('aria-current', 'step'); else s.removeAttribute('aria-current');
        const end = i + 1 < at.length ? at[i + 1] : dur;
        const p = i < cur ? 1 : on && end > at[i] ? clamp((t - at[i]) / (end - at[i])) : 0;
        const bar = $('.xp-hiw-step__bar i', s);
        if (bar) bar.style.setProperty('--p', p.toFixed(3));
      });
    }
    const loop = () => { raf = 0; const v = shown(); update(v); if (!v.paused) raf = w.requestAnimationFrame(loop); };
    function start(v) {
      if (v.preload === 'none') v.preload = 'auto';
      v.muted = true;
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    }
    function sync() {
      const v = shown();
      vids.forEach((o) => { if (o !== v && !o.paused) o.pause(); });
      if (wants()) start(v); else if (!v.paused) v.pause();
    }
    vids.forEach((v) => {
      v.addEventListener('playing', () => {
        if (v !== shown()) return;
        film.classList.add('is-live', 'is-playing');
        film.classList.remove('is-idle');
        btn.setAttribute('aria-label', 'Pause the demo');
        if (!raf) raf = w.requestAnimationFrame(loop);
      });
      v.addEventListener('pause', () => {
        if (v !== shown()) return;
        film.classList.remove('is-playing');
        btn.setAttribute('aria-label', 'Play the demo');
      });
      v.addEventListener('seeked', () => update(v));
    });
    btn.addEventListener('click', () => {
      const v = shown();
      if (v.paused) { userPaused = false; userPlay = true; start(v); } else { userPaused = true; v.pause(); }
    });
    steps.forEach((s) => s.addEventListener('click', () => {
      const v = shown();
      const t = (Number(s.dataset[key(v)]) || 0) + 0.01;
      userPaused = false;
      userPlay = true;
      const go = () => { try { v.currentTime = t; } catch (e) { /* not seekable yet */ } update(v); start(v); };
      if (v.readyState >= 1) go();
      else { v.addEventListener('loadedmetadata', go, { once: true }); v.preload = 'auto'; v.load(); }
    }));
    if ('IntersectionObserver' in w) {
      new IntersectionObserver((es) => es.forEach((e) => { visible = e.intersectionRatio >= 0.5; sync(); }), { threshold: [0, 0.5] }).observe(film);
    }
    d.addEventListener('visibilitychange', sync);
    rmq.addEventListener('change', () => { idle(); sync(); });
    w.matchMedia('(max-width: 767px)').addEventListener('change', () => { film.classList.remove('is-live', 'is-playing'); update(shown()); sync(); });
    if (!loaded) w.addEventListener('load', () => { loaded = true; sync(); }, { once: true });
  }

  const init = () => $$('[data-hiw]').forEach(initHow);
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', init, { once: true }); else init();
})();
